import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, ChevronRight, Star, Shield, Car, FileText, HelpCircle,
  LogOut, Bell, Moon, Sun, Globe, CreditCard, Settings, Award,
  Phone, Mail, MapPin, Calendar, CheckCircle
} from 'lucide-react';
import { MobileCard, MobileButton } from '../components/MobileUIKit';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { useMobileTheme } from '../context/MobileThemeContext';
import '../styles/mobile.css';

// Mock profile data
const mockProfile = {
  name: 'Carlos Rodriguez',
  email: 'carlos.r@ezparcel.com',
  phone: '+55 11 99999-8888',
  avatar: null,
  rating: 4.9,
  totalDeliveries: 1247,
  memberSince: 'March 2024',
  status: 'Active',
  badges: ['Top Driver', 'Night Owl', 'Perfect Week'],
  vehicle: {
    model: 'Honda Civic 2022',
    plate: 'ABC-1234',
    color: 'Silver'
  },
  documents: {
    license: { status: 'valid', expires: '2026-12-15' },
    insurance: { status: 'valid', expires: '2025-06-30' },
    vehicle: { status: 'valid', expires: '2025-08-20' }
  }
};

const menuSections = [
  {
    title: 'Account',
    items: [
      { id: 'profile', icon: User, label: 'Edit Profile', badge: null },
      { id: 'vehicle', icon: Car, label: 'Vehicle Info', badge: null },
      { id: 'documents', icon: FileText, label: 'Documents', badge: '1 expiring' },
      { id: 'payments', icon: CreditCard, label: 'Payment Methods', badge: null },
    ]
  },
  {
    title: 'Preferences',
    items: [
      { id: 'notifications', icon: Bell, label: 'Notifications', badge: null },
      { id: 'language', icon: Globe, label: 'Language', badge: 'English' },
    ]
  },
  {
    title: 'Support',
    items: [
      { id: 'help', icon: HelpCircle, label: 'Help & Support', badge: null },
      { id: 'safety', icon: Shield, label: 'Safety', badge: null },
    ]
  }
];

export const DriverProfilePage = () => {
  const navigate = useNavigate();
  const { isDark, setTheme } = useMobileTheme();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  return (
    <div className="mobile-app min-h-screen pb-20">
      {/* Header */}
      <div 
        className="mobile-safe-top px-4 pt-4 pb-8"
        style={{ 
          background: 'linear-gradient(180deg, var(--ezp-primary) 0%, var(--ezp-primary-dark) 100%)'
        }}
      >
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold"
            style={{ 
              background: 'rgba(255,255,255,0.2)',
              border: '3px solid rgba(255,255,255,0.5)'
            }}
          >
            {mockProfile.name.split(' ').map(n => n[0]).join('')}
          </div>
          
          {/* Profile Info */}
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">{mockProfile.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-white/90 font-semibold">{mockProfile.rating}</span>
              <span className="text-white/60 text-sm">• {mockProfile.totalDeliveries} deliveries</span>
            </div>
            <div className="flex gap-1 mt-2">
              {mockProfile.badges.slice(0, 2).map((badge, i) => (
                <span 
                  key={i}
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-4 -mt-4">
        <MobileCard elevated className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold" style={{ color: 'var(--mobile-earnings)' }}>
              {mockProfile.totalDeliveries}
            </p>
            <p className="mobile-text-tiny">Deliveries</p>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: 'var(--mobile-warning)' }}>
              {mockProfile.rating}
            </p>
            <p className="mobile-text-tiny">Rating</p>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: 'var(--mobile-info)' }}>
              98%
            </p>
            <p className="mobile-text-tiny">On-Time</p>
          </div>
        </MobileCard>
      </div>

      {/* Theme Toggle */}
      <div className="px-4 mt-4">
        <MobileCard 
          interactive
          className="flex items-center justify-between"
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
        >
          <div className="flex items-center gap-3">
            {isDark ? (
              <Moon className="w-5 h-5" style={{ color: 'var(--mobile-info)' }} />
            ) : (
              <Sun className="w-5 h-5" style={{ color: 'var(--mobile-warning)' }} />
            )}
            <span style={{ color: 'var(--mobile-text-primary)' }}>
              {isDark ? 'Dark Mode' : 'Light Mode'}
            </span>
          </div>
          <div 
            className={`w-12 h-6 rounded-full p-1 transition-colors ${isDark ? 'bg-[var(--mobile-info)]' : 'bg-[var(--mobile-bg-tertiary)]'}`}
          >
            <div 
              className={`w-4 h-4 rounded-full bg-white transition-transform ${isDark ? 'translate-x-6' : 'translate-x-0'}`}
            />
          </div>
        </MobileCard>
      </div>

      {/* Menu Sections */}
      {menuSections.map(section => (
        <div key={section.title} className="px-4 mt-6">
          <h3 
            className="mobile-text-caption font-semibold mb-2 uppercase"
            style={{ color: 'var(--mobile-text-tertiary)' }}
          >
            {section.title}
          </h3>
          <MobileCard className="divide-y" style={{ borderColor: 'var(--mobile-divider)' }}>
            {section.items.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className="w-full flex items-center gap-3 py-3 px-1 text-left"
                  onClick={() => {/* Handle navigation */}}
                >
                  <Icon className="w-5 h-5" style={{ color: 'var(--mobile-text-secondary)' }} />
                  <span className="flex-1" style={{ color: 'var(--mobile-text-primary)' }}>
                    {item.label}
                  </span>
                  {item.badge && (
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ 
                        background: item.badge === '1 expiring' 
                          ? 'var(--mobile-warning-muted)' 
                          : 'var(--mobile-bg-tertiary)',
                        color: item.badge === '1 expiring'
                          ? 'var(--mobile-warning)'
                          : 'var(--mobile-text-secondary)'
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4" style={{ color: 'var(--mobile-text-disabled)' }} />
                </button>
              );
            })}
          </MobileCard>
        </div>
      ))}

      {/* Vehicle Info Card */}
      <div className="px-4 mt-6">
        <h3 
          className="mobile-text-caption font-semibold mb-2 uppercase"
          style={{ color: 'var(--mobile-text-tertiary)' }}
        >
          My Vehicle
        </h3>
        <MobileCard className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'var(--mobile-bg-tertiary)' }}
          >
            <Car className="w-6 h-6" style={{ color: 'var(--mobile-text-secondary)' }} />
          </div>
          <div className="flex-1">
            <p className="font-medium" style={{ color: 'var(--mobile-text-primary)' }}>
              {mockProfile.vehicle.model}
            </p>
            <p className="mobile-text-tiny">
              {mockProfile.vehicle.plate} • {mockProfile.vehicle.color}
            </p>
          </div>
          <CheckCircle className="w-5 h-5" style={{ color: 'var(--mobile-success)' }} />
        </MobileCard>
      </div>

      {/* Logout Button */}
      <div className="px-4 mt-8 mb-24">
        <MobileButton
          variant="ghost"
          fullWidth
          onClick={() => setShowLogoutConfirm(true)}
          className="text-red-500"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Log Out
        </MobileButton>
        
        <p className="text-center mobile-text-tiny mt-4" style={{ color: 'var(--mobile-text-disabled)' }}>
          EzParcel Driver v2.0.0
        </p>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowLogoutConfirm(false)}
          />
          <MobileCard elevated className="relative z-10 w-full max-w-sm text-center">
            <LogOut className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--mobile-error)' }} />
            <h3 className="mobile-text-subtitle mb-2" style={{ color: 'var(--mobile-text-primary)' }}>
              Log Out?
            </h3>
            <p className="mobile-text-caption mb-6">
              Are you sure you want to log out of your account?
            </p>
            <div className="flex gap-3">
              <MobileButton
                variant="secondary"
                fullWidth
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </MobileButton>
              <MobileButton
                variant="primary"
                fullWidth
                onClick={handleLogout}
                style={{ background: 'var(--mobile-error)' }}
              >
                Log Out
              </MobileButton>
            </div>
          </MobileCard>
        </div>
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default DriverProfilePage;
